import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { emailService } from '@/services/emailService';
import {
  orchestratorService,
  type PendingEmailDraft,
  type PendingDraftResolution,
  type ResolvePendingDraftResult,
} from '@/services/orchestratorService';
import { CACHE_DURATION } from '../utils/cache';

// ─── Types ──────────────────────────────────────────────────

export interface ScheduledEmail {
  id: string;
  to: string;
  subject: string;
  html?: string | null;
  text?: string | null;
  scheduledAt: string;
  createdAt: string;
  updatedAt?: string;
  projectId?: string;
  flowId?: string;
  stepId?: string;
  taskName?: string | null;
  conversationId?: string;
}

interface EmailState {
  mailboxPreferences: Record<string, MailboxPreference>;
  mailboxOwner: string | null;
  scheduledEmails: ScheduledEmail[];
  starredIds: string[];
  aiDrafts: PendingEmailDraft[];
  aiDraftsLoading: boolean;
  aiDraftsLastFetched: number | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: EmailState = {
  mailboxPreferences: {},
  mailboxOwner: null,
  scheduledEmails: [],
  starredIds: [],
  aiDrafts: [],
  aiDraftsLoading: false,
  aiDraftsLastFetched: null,
  isLoading: false,
  isSaving: false,
  error: null,
  lastFetched: null,
};

export interface MailboxPreference {
  isRead?: boolean;
  isStarred?: boolean;
  folder?: 'drafts' | 'archived' | 'deleted';
}

export const loadMailboxPreferences = createAsyncThunk(
  'email/loadMailboxPreferences',
  async (uid: string) => {
    const raw = localStorage.getItem(`drahft_mailbox_${uid}`);
    if (!raw) return { uid, preferences: {} as Record<string, MailboxPreference> };
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Invalid saved mailbox preferences');
    }
    const preferences: Record<string, MailboxPreference> = {};
    Object.entries(parsed).forEach(([id, value]) => {
      if (!value || typeof value !== 'object') throw new Error('Invalid mailbox item');
      const { isRead, isStarred, folder } = value;
      if (
        (isRead !== undefined && typeof isRead !== 'boolean') ||
        (isStarred !== undefined && typeof isStarred !== 'boolean') ||
        (folder !== undefined && !['drafts', 'archived', 'deleted'].includes(folder))
      )
        throw new Error('Invalid mailbox item preferences');
      preferences[id] = { isRead, isStarred, folder };
    });
    return { uid, preferences };
  }
);

export const saveMailboxPreferences = createAsyncThunk(
  'email/saveMailboxPreferences',
  async (
    { uid, updates }: { uid: string; updates: Record<string, MailboxPreference> },
    { getState }
  ) => {
    const state = (getState() as { email: EmailState }).email;
    if (state.mailboxOwner !== uid) throw new Error('Mailbox preferences are not loaded');
    const preferences = { ...state.mailboxPreferences };
    Object.entries(updates).forEach(([id, update]) => {
      preferences[id] = { ...preferences[id], ...update };
    });
    localStorage.setItem(`drahft_mailbox_${uid}`, JSON.stringify(preferences));
    return { uid, preferences };
  }
);

// ─── Async Thunks ───────────────────────────────────────────

export const fetchScheduledEmails = createAsyncThunk(
  'email/fetchScheduled',
  async (options: { force?: boolean } | undefined, { getState, rejectWithValue }) => {
    const force = options?.force ?? false;
    const state = (getState() as { email: EmailState }).email;
    const now = Date.now();

    if (
      !force &&
      state.scheduledEmails.length > 0 &&
      state.lastFetched &&
      now - state.lastFetched < CACHE_DURATION
    ) {
      return state.scheduledEmails;
    }

    try {
      const response = await emailService.getScheduledEmails();

      const data = (response as any)?.data ?? response;
      return Array.isArray(data) ? data : [];
    } catch (err) {
      return rejectWithValue(
        err instanceof Error ? err.message : 'Failed to load scheduled emails'
      );
    }
  }
);

export const cancelScheduledEmail = createAsyncThunk(
  'email/cancelScheduled',
  async (id: string, { rejectWithValue }) => {
    try {
      await emailService.cancelScheduledEmail(id);
      return id;
    } catch (err) {
      return rejectWithValue(
        err instanceof Error ? err.message : 'Failed to cancel scheduled email'
      );
    }
  }
);

export const updateScheduledEmail = createAsyncThunk(
  'email/updateScheduled',
  async (
    {
      id,
      subject,
      html,
      text,
      scheduledAt,
    }: { id: string; subject?: string; html?: string; text?: string; scheduledAt?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await emailService.updateScheduledEmail(id, {
        subject,
        html,
        text,
        scheduledAt,
      });
      const data = (response as any)?.data ?? response;
      return data as ScheduledEmail;
    } catch (err) {
      return rejectWithValue(
        err instanceof Error ? err.message : 'Failed to update scheduled email'
      );
    }
  }
);

export const fetchAiEmailDrafts = createAsyncThunk(
  'email/fetchAiDrafts',
  async (options: { force?: boolean } | undefined, { getState, rejectWithValue }) => {
    const force = options?.force ?? false;
    const state = (getState() as { email: EmailState }).email;
    const now = Date.now();

    if (!force && state.aiDraftsLastFetched && now - state.aiDraftsLastFetched < CACHE_DURATION) {
      return state.aiDrafts;
    }

    try {
      const data = await orchestratorService.listPendingEmailDrafts();
      return Array.isArray(data) ? data : [];
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to load AI drafts');
    }
  }
);

export const resolveAiEmailDraft = createAsyncThunk(
  'email/resolveAiDraft',
  async (
    {
      projectId,
      actionId,
      resolution,
      editedPayload,
    }: {
      projectId: string;
      actionId: string;
      resolution: PendingDraftResolution;
      editedPayload?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const result = await orchestratorService.resolvePendingAction(
        projectId,
        actionId,
        resolution,
        editedPayload
      );
      return { actionId, editedPayload, result: result as ResolvePendingDraftResult };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to resolve AI draft');
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────

const emailSlice = createSlice({
  name: 'email',
  initialState,
  reducers: {
    toggleStarred: (state, action: PayloadAction<string>) => {
      const idx = state.starredIds.indexOf(action.payload);
      if (idx === -1) {
        state.starredIds.push(action.payload);
      } else {
        state.starredIds.splice(idx, 1);
      }
    },
    clearEmail: state => {
      state.mailboxPreferences = {};
      state.mailboxOwner = null;
      state.scheduledEmails = [];
      state.starredIds = [];
      state.aiDrafts = [];
      state.aiDraftsLastFetched = null;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadMailboxPreferences.pending, state => {
        state.mailboxOwner = null;
        state.mailboxPreferences = {};
      })
      .addCase(loadMailboxPreferences.fulfilled, (state, action) => {
        state.mailboxOwner = action.payload.uid;
        state.mailboxPreferences = action.payload.preferences;
      })
      .addCase(loadMailboxPreferences.rejected, (state, action) => {
        state.error = action.error.message ?? 'Failed to load mailbox preferences';
      })
      .addCase(saveMailboxPreferences.fulfilled, (state, action) => {
        if (state.mailboxOwner === action.payload.uid) {
          state.mailboxPreferences = action.payload.preferences;
        }
      })
      .addCase(saveMailboxPreferences.rejected, (state, action) => {
        state.error = action.error.message ?? 'Failed to save mailbox preferences';
      });
    // fetchScheduledEmails
    builder
      .addCase(fetchScheduledEmails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchScheduledEmails.fulfilled, (state, action) => {
        state.scheduledEmails = action.payload;
        state.isLoading = false;
        state.lastFetched = Date.now();
      })
      .addCase(fetchScheduledEmails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) ?? 'Failed to load scheduled emails';
      });

    // cancelScheduledEmail
    builder
      .addCase(cancelScheduledEmail.pending, state => {
        state.isSaving = true;
      })
      .addCase(cancelScheduledEmail.fulfilled, (state, action) => {
        state.scheduledEmails = state.scheduledEmails.filter(e => e.id !== action.payload);
        state.isSaving = false;
      })
      .addCase(cancelScheduledEmail.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to cancel scheduled email';
      });

    // updateScheduledEmail
    builder
      .addCase(updateScheduledEmail.pending, state => {
        state.isSaving = true;
      })
      .addCase(updateScheduledEmail.fulfilled, (state, action) => {
        const idx = state.scheduledEmails.findIndex(e => e.id === action.payload.id);
        if (idx !== -1) state.scheduledEmails[idx] = action.payload;
        state.isSaving = false;
      })
      .addCase(updateScheduledEmail.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to update scheduled email';
      });

    // fetchAiEmailDrafts
    builder
      .addCase(fetchAiEmailDrafts.pending, state => {
        state.aiDraftsLoading = true;
      })
      .addCase(fetchAiEmailDrafts.fulfilled, (state, action) => {
        state.aiDrafts = action.payload;
        state.aiDraftsLoading = false;
        state.aiDraftsLastFetched = Date.now();
      })
      .addCase(fetchAiEmailDrafts.rejected, (state, action) => {
        state.aiDraftsLoading = false;
        state.error = (action.payload as string) ?? 'Failed to load AI drafts';
      });

    // resolveAiEmailDraft — remove the resolved draft from the pending list
    builder
      .addCase(resolveAiEmailDraft.pending, state => {
        state.isSaving = true;
      })
      .addCase(resolveAiEmailDraft.fulfilled, (state, action) => {
        if (action.meta.arg.resolution === 'updated') {
          const draft = state.aiDrafts.find(d => d.id === action.payload.actionId);
          if (draft && action.payload.editedPayload) {
            draft.payload = action.payload.editedPayload;
            draft.updated_at = new Date().toISOString();
          }
        } else {
          state.aiDrafts = state.aiDrafts.filter(d => d.id !== action.payload.actionId);
        }
        state.isSaving = false;
      })
      .addCase(resolveAiEmailDraft.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to resolve AI draft';
      });
  },
});

export const { toggleStarred, clearEmail } = emailSlice.actions;
export default emailSlice.reducer;
